import React, { useState, useEffect } from 'react';
import { financeAPI } from '../../lib/api';
import { Plus, Edit, Trash2, Search, RefreshCw } from 'lucide-react';

const BankAccounts = () => {
    const [accounts, setAccounts] = useState([]);
    const [chartOfAccounts, setChartOfAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [formData, setFormData] = useState({
        chart_of_account_id: '',
        account_name: '',
        account_number: '',
        bank_name: '',
        branch_name: '',
        opening_balance: '',
        opening_balance_date: '',
        is_active: true
    });

    useEffect(() => { 
        fetchAccounts(); 
        fetchChartOfAccounts();
    }, []);

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            const response = await financeAPI.getBankAccounts();
            setAccounts(response.data.data || response.data);
        } catch (err) { setError('Failed to load data'); }
        finally { setLoading(false); }
    };

    const fetchChartOfAccounts = async () => {
        try {
            const response = await financeAPI.getChartOfAccounts();
            const data = response.data.data || response.data;
            // Filter for Asset type accounts that are typically used for bank accounts
            const activeAccounts = (Array.isArray(data) ? data : data.data || []).filter(
                acc => acc.is_active !== false
            );
            setChartOfAccounts(activeAccounts);
        } catch (err) { 
            console.error('Failed to load chart of accounts'); 
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await financeAPI.updateBankAccount(editingId, formData);
            } else {
                await financeAPI.createBankAccount(formData);
            }
            setShowModal(false);
            resetForm();
            fetchAccounts();
        } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    };

    const handleEdit = (acc) => {
        setFormData({
            chart_of_account_id: acc.chart_of_account_id || '',
            account_name: acc.account_name,
            account_number: acc.account_number,
            bank_name: acc.bank_name,
            branch_name: acc.branch_name || '',
            opening_balance: acc.opening_balance || '',
            opening_balance_date: acc.opening_balance_date || '',
            is_active: acc.is_active !== false
        });
        setEditingId(acc.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            await financeAPI.deleteBankAccount(id);
            fetchAccounts();
        } catch (err) { setError('Failed to delete'); }
    };

    const resetForm = () => {
        setFormData({ 
            chart_of_account_id: '',
            account_name: '', 
            account_number: '', 
            bank_name: '', 
            branch_name: '', 
            opening_balance: '', 
            opening_balance_date: '',
            is_active: true 
        });
        setEditingId(null);
    };

    const filteredAccounts = accounts.filter(acc => 
        acc.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        acc.bank_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Bank Accounts</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage bank accounts</p>
                </div>
                <button onClick={() => { resetForm(); setShowModal(true); }} className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                    <Plus className="w-4 h-4 mr-2" /> Add Account
                </button>
            </div>

            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
            </div>

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Chart of Account</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Account Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Account Number</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Bank Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Opening Balance</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredAccounts.length > 0 ? filteredAccounts.map(acc => (
                                    <tr key={acc.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                            {acc.chart_of_account?.account_name || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200 font-medium">{acc.account_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 font-mono">{acc.account_number}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{acc.bank_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                            {acc.opening_balance_posted ? (
                                                <span className="text-green-600 font-medium">{acc.opening_balance || 0}</span>
                                            ) : (
                                                <span>{acc.opening_balance || 0}</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {acc.opening_balance_posted ? (
                                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Posted</span>
                                            ) : (
                                                <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">Pending</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button onClick={() => handleEdit(acc)} className="text-indigo-600 hover:text-indigo-900 mr-3"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(acc.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No accounts found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{editingId ? 'Edit' : 'Add'} Bank Account</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chart of Account <span className="text-red-500">*</span></label>
                                <select 
                                    value={formData.chart_of_account_id} 
                                    onChange={(e) => setFormData({...formData, chart_of_account_id: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200"
                                    required
                                >
                                    <option value="">Select Chart of Account</option>
                                    {chartOfAccounts.map(coa => (
                                        <option key={coa.id} value={coa.id}>
                                            {coa.account_code} - {coa.account_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Name</label>
                                <input type="text" value={formData.account_name} onChange={(e) => setFormData({...formData, account_name: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Number</label>
                                <input type="text" value={formData.account_number} onChange={(e) => setFormData({...formData, account_number: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bank Name</label>
                                <input type="text" value={formData.bank_name} onChange={(e) => setFormData({...formData, bank_name: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Branch Name</label>
                                <input type="text" value={formData.branch_name} onChange={(e) => setFormData({...formData, branch_name: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Opening Balance</label>
                                <input type="number" step="0.01" min="0" value={formData.opening_balance} onChange={(e) => setFormData({...formData, opening_balance: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" placeholder="0.00" />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Opening Balance Date</label>
                                <input type="date" value={formData.opening_balance_date} onChange={(e) => setFormData({...formData, opening_balance_date: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                            </div>
                            <div className="mb-3 flex items-center">
                                <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="mr-2" />
                                <label className="text-sm text-gray-700 dark:text-gray-300">Active</label>
                            </div>
                            <div className="flex justify-end gap-3 mt-4">
                                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">{editingId ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BankAccounts;
